import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService, CoursesService, PathsService, PortalConfigurationService, UrlService } from '@draeger-academy/common';
import { AscService } from '@draeger-academy/common/lib/services/acs-input/acs.service';
import { Catalog } from 'models/catalog';
import { Course } from 'models/course';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';

@Component({
    selector: 'da-acs-input',
    templateUrl: './acs-input.component.html',
    styleUrls: ['./acs-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AcsInputComponent implements OnInit {
    @Input() coursesByTitle: Course[];
    @Output()
    titleValue: EventEmitter<string> = new EventEmitter<string>();
    public hasFocusTitle = false;
    @Input()title = '';;
    isPortal = null;
    portalLanguage = null;
    currentIndex;
    private _site = null;
    private _$showResults: BehaviorSubject<any> = new BehaviorSubject<boolean>(null);
    public get showResults$(): Observable<any> {
        return this._$showResults.asObservable();
    }
    constructor(
        public config: ConfigurationService,
        private asc: AscService,
        private _coursesService: CoursesService,
        private _portalConfigurationService: PortalConfigurationService,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService,
        private _router: Router,
        private elementRef: ElementRef,
        private activatedRoute: ActivatedRoute,
        private _urlService: UrlService
    ) {}

    ngOnInit() {
        this._setTitle();
        this._configurationService.portalLanguage$.subscribe(() => {
            this._setTitle();
        });
    }

    private _setTitle() {
        this.getParameterFromURL();
    }

    public valuechange(valueTyped) {
        this.titleValue.emit(valueTyped);
    }

    public reset() {
        this.title = '';
        this.titleValue.emit(this.title);
    }

    public hasValue(fieldname) {
        return fieldname ? fieldname.toString() !== '' : false;
    }

    public getCourses(): Promise<any> {
        return this._coursesService.getAllCourses().then(
            (courses) => {
                return courses;
            },
            (error) => {
                console.log(error);
            }
        );
    }

    private _getPortalCourses(portal: string): Promise<any> {
        const _portal = this._portalConfigurationService.getPortalIdWithUnderscore(portal);
        return this._coursesService.getAllPortalCourses(_portal).then(
            (courses) => {
                return courses;
            },
            (error) => {
                console.log(error);
            }
        );
    }

    public searchListSuggestions(selectedValue) {
        this.hasFocusTitle = true;
        this._$showResults.next([]);
        if (selectedValue && selectedValue.length >= 3) {
            const tempResults = [];
            const checkUniqueTitle = [];
            this.coursesByTitle.filter((course) => {
                const name = course.name.toLocaleLowerCase();
                if (name.indexOf(selectedValue.toLowerCase()) > -1) {
                    if (!checkUniqueTitle.includes(name)) {
                        tempResults.push(course);
                        checkUniqueTitle.push(name);
                    }
                }
            });
            this._$showResults.next(tempResults);
        }
    }

    onClickedOutside(e: Event) {
        this.hasFocusTitle = false;
    }

    public clickLink(id: string, name: string) {
        this.title = name;
        this.valuechange(name);
        this.hasFocusTitle = false;
    }

    public getHighlightedString(result: any, searchTerm: string): string {
        if (!this.title) {
            return result;
        }

        const currentSearchTerms = this.title
            .trim()
            .replace(/ +/g, ' ')
            .replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
            .split(' ')
            .join('|');
        const searchRegExp = new RegExp(`(^(${currentSearchTerms})|\\W(${currentSearchTerms}))`, 'gi');
        return result.replace(searchRegExp, (matched) => {
            return matched.charAt(0) === ' ' ? ` <strong>${matched.slice(1)}</strong>` : `<strong>${matched}</strong>`;
        });
    }

    public enterPressed(e) {
        if (e.keyCode === 13) {
            this.hasFocusTitle = false;
            if (e.target.value === '') {
                this.hasFocusTitle = true;
            }
        } else {
            this.hasFocusTitle = true;
        }
        this.valuechange(e.target.value);
    }

    public resetTitle() {
        this.title = '';
        this._$showResults.next([]);
        this.valuechange(null);
    }
    public listFocus(e) {
        if (e.keyCode === 40) {
            document.getElementById('element0').focus();
            document.getElementById('element0').classList.add('active');
            this.currentIndex = 0;
        }
    }

    public listFocusNext(e) {
        const lengthList = document.getElementsByClassName('search-element').length;
        if (document.getElementById('element' + this.currentIndex)) {
            document.getElementById('element' + this.currentIndex).classList.remove('active');
        }
        if (e.keyCode === 40) {
            this.currentIndex = this.currentIndex + 1;

            if (this.currentIndex <= lengthList - 1 && this.currentIndex >= 0 && document.getElementById('element' + this.currentIndex)) {
                document.getElementById('element' + this.currentIndex).focus();
                document.getElementById('element' + this.currentIndex).classList.add('active');
            } else {
                this.currentIndex = 0;
                if (document.getElementById('element' + this.currentIndex)) {
                    document.getElementById('element' + this.currentIndex).focus();
                    document.getElementById('element' + this.currentIndex).classList.add('active');
                }
            }
        } else if (e.keyCode === 38) {
            this.currentIndex = this.currentIndex - 1;
            if (this.currentIndex <= lengthList - 1 && this.currentIndex >= 0 && document.getElementById('element' + this.currentIndex)) {
                document.getElementById('element' + this.currentIndex).focus();
                document.getElementById('element' + this.currentIndex).classList.add('active');
            } else {
                this.currentIndex = lengthList - 1;
                if (document.getElementById('element' + this.currentIndex)) {
                    document.getElementById('element' + this.currentIndex).focus();
                    document.getElementById('element' + this.currentIndex).classList.add('active');
                }
            }
        }
    }

    @HostListener('document:click', ['$event.target'])
    public onPageClick(targetElement) {
        const clickedInside = this.elementRef.nativeElement.contains(targetElement);
        if (targetElement.nodeName === 'BUTTON' || !clickedInside) {
            this.hasFocusTitle = false;
        } else {
            this.hasFocusTitle = true;
        }
    }

    private getParameterFromURL() {
        const params = this._urlService.getURLParams();
        this.title = params.title;
    }
}
